import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cancelTradeGroup } from '../api/tradeGroups';
import { CancelTradeGroupRequest, CancelTradeGroupResponse } from '../types/tradeGroup';
import './CancelTradeGroupModal.css';

interface CancelTradeGroupModalProps {
  tradeGroupId: string;
  productId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CancelTradeGroupModal: React.FC<CancelTradeGroupModalProps> = ({
  tradeGroupId,
  productId,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [tradeSide, setTradeSide] = useState<'BUY' | 'SELL' | ''>('');
  const [previewResponse, setPreviewResponse] = useState<CancelTradeGroupResponse | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const cancelMutation = useMutation({
    mutationFn: (request: CancelTradeGroupRequest) => cancelTradeGroup(request),
    onSuccess: (data) => {
      // If tradesPreviewed is not null, this is a preview response
      if (data.tradesPreviewed !== null && data.tradesPreviewed !== undefined) {
        // Show preview results
        setPreviewResponse(data);
        setShowConfirmation(true);
      } else {
        // Actual cancellation succeeded
        queryClient.invalidateQueries({ queryKey: ['active-trade-groups-details'] });
        queryClient.invalidateQueries({ queryKey: ['trade-group-details', tradeGroupId] });
        onSuccess();
        onClose();
      }
    },
    onError: (error) => {
      console.error('Error canceling trade group:', error);
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    },
  });

  const handlePreview = () => {
    const request: CancelTradeGroupRequest = {
      tradeGroupId,
      productId,
      minPrice: minPrice.trim() || null,
      maxPrice: maxPrice.trim() || null,
      tradeSide: tradeSide || null,
      preview: true,
    };
    cancelMutation.mutate(request);
  };

  const handleApply = () => {
    // Apply the cancellation
    const request: CancelTradeGroupRequest = {
      tradeGroupId,
      productId,
      minPrice: minPrice.trim() || null,
      maxPrice: maxPrice.trim() || null,
      tradeSide: tradeSide || null,
      preview: false,
    };
    cancelMutation.mutate(request);
  };

  const handleBack = () => {
    setShowConfirmation(false);
    setPreviewResponse(null);
  };

  const formatPrice = (price: string) => {
    return parseFloat(price).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content cancel-trade-group-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{showConfirmation ? 'Confirm Cancellation' : 'Cancel Trade Group'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {!showConfirmation ? (
          <div className="modal-body">
            <p className="modal-description">
              Cancel all or a subset of orders in this trade group. You can filter by price range and trade side.
            </p>

            <div className="form-group">
              <label>Min Price (Optional)</label>
              <input
                type="number"
                step="0.01"
                placeholder="e.g., 50000"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className="form-input"
              />
              <small className="form-hint">Only cancel orders with price ≥ this value</small>
            </div>

            <div className="form-group">
              <label>Max Price (Optional)</label>
              <input
                type="number"
                step="0.01"
                placeholder="e.g., 60000"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="form-input"
              />
              <small className="form-hint">Only cancel orders with price ≤ this value</small>
            </div>

            <div className="form-group">
              <label>Trade Side (Optional)</label>
              <select
                value={tradeSide}
                onChange={(e) => setTradeSide(e.target.value as 'BUY' | 'SELL' | '')}
                className="form-select"
              >
                <option value="">Both Buy and Sell</option>
                <option value="BUY">Buy Only</option>
                <option value="SELL">Sell Only</option>
              </select>
              <small className="form-hint">Filter by order side</small>
            </div>
          </div>
        ) : (
          <div className="modal-body preview-section">
            <div className="preview-summary">
              <h3>Preview Results</h3>
              <p>{previewResponse?.message}</p>
              <div className="preview-counts">
                <div className="count-item">
                  <span className="count-label">Buy Orders:</span>
                  <span className="count-value">{previewResponse?.buyOrdersCancelled || 0}</span>
                </div>
                <div className="count-item">
                  <span className="count-label">Sell Orders:</span>
                  <span className="count-value">{previewResponse?.sellOrdersCancelled || 0}</span>
                </div>
              </div>
            </div>

            {previewResponse?.tradesPreviewed && previewResponse.tradesPreviewed.length > 0 && (
              <div className="preview-trades">
                <h4>Orders to be Cancelled:</h4>
                <div className="trades-table-container">
                  <table className="trades-table">
                    <thead>
                      <tr>
                        <th>Side</th>
                        <th>Limit Price</th>
                        <th>Base Size</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewResponse.tradesPreviewed.map((trade, index) => (
                        <tr key={index}>
                          <td>
                            <span className={`side-badge ${trade.side.toLowerCase()}`}>
                              {trade.side}
                            </span>
                          </td>
                          <td>{formatPrice(trade.limitPrice)}</td>
                          <td>{parseFloat(trade.baseSize).toFixed(8)}</td>
                          <td>{trade.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="modal-footer">
          {!showConfirmation ? (
            <>
              <button
                className="modal-pill-button modal-pill-cancel"
                onClick={onClose}
                disabled={cancelMutation.isPending}
              >
                Cancel
              </button>
              <button
                className="modal-pill-button modal-pill-preview"
                onClick={handlePreview}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? 'Loading...' : 'Preview'}
              </button>
              <button
                className="modal-pill-button modal-pill-apply"
                onClick={handleApply}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? 'Applying...' : 'Apply'}
              </button>
            </>
          ) : (
            <>
              <button
                className="modal-pill-button modal-pill-cancel"
                onClick={handleBack}
                disabled={cancelMutation.isPending}
              >
                Back
              </button>
              <button
                className="modal-pill-button modal-pill-apply"
                onClick={handleApply}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? 'Applying...' : 'Apply'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancelTradeGroupModal;
